import { ref } from 'vue'
import { useConvexClient } from 'convex-vue'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

export interface FileUploadMetadata {
  fileId: Id<'_storage'>
  fileName: string
  fileType: string
  fileSizeBytes: number
}

export interface UploadProgress {
  fileName: string
  progress: number
  loaded: number
  total: number
}

export function useConvexFileUpload() {
  const convex = useConvexClient()
  const isUploading = ref(false)
  const uploadProgress = ref(0)
  const error = ref<string | null>(null)

  // Track individual file uploads with abort controllers
  const activeUploads = new Map<string, AbortController>()
  const fileProgress = ref<Map<string, UploadProgress>>(new Map())

  async function uploadFile(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<FileUploadMetadata | null> {
    isUploading.value = true
    uploadProgress.value = 0
    error.value = null

    const abortController = new AbortController()
    activeUploads.set(file.name, abortController)

    try {
      // Generate upload URL from Convex using the mutation
      const uploadUrl = await convex.mutation(api.fileProcessingHelpers.generateUploadUrl, {})
      console.log('[useFileUpload] Generated upload URL:', uploadUrl)

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest()

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          uploadProgress.value = progress

          const progressData: UploadProgress = {
            fileName: file.name,
            progress,
            loaded: event.loaded,
            total: event.total
          }

          fileProgress.value.set(file.name, progressData)

          if (onProgress) {
            onProgress(progressData)
          }

          console.log(`[useFileUpload] Upload progress for ${file.name}:`, progress + '%')
        }
      })

      // Set up abort handler
      abortController.signal.addEventListener('abort', () => {
        xhr.abort()
      })

      // Upload the file
      const uploadPromise = new Promise<Response>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Create a Response-like object
            const response = {
              ok: true,
              status: xhr.status,
              json: async () => JSON.parse(xhr.responseText)
            } as Response
            resolve(response)
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`))
          }
        }

        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.onabort = () => reject(new Error('Upload cancelled'))

        xhr.open('POST', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })

      const uploadResponse = await uploadPromise

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file')
      }

      const responseData = await uploadResponse.json()
      console.log('[useFileUpload] Upload response:', responseData)

      const { storageId: fileId } = responseData

      if (!fileId) {
        console.error('[useFileUpload] No storageId in response:', responseData)
        throw new Error('No storage ID returned from upload')
      }

      console.log('[useFileUpload] File uploaded successfully:', { fileId, fileName: file.name })

      uploadProgress.value = 100

      return {
        fileId: fileId as Id<'_storage'>,
        fileName: file.name,
        fileType: file.type,
        fileSizeBytes: file.size
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Upload cancelled') {
        console.log('[useFileUpload] Upload cancelled:', file.name)
        return null
      }
      error.value = err instanceof Error ? err.message : 'Unknown error occurred'
      console.error('File upload error:', err)
      return null
    } finally {
      isUploading.value = false
      activeUploads.delete(file.name)
      fileProgress.value.delete(file.name)
    }
  }

  function cancelUpload(fileName: string) {
    const controller = activeUploads.get(fileName)
    if (controller) {
      console.log('[useFileUpload] Cancelling upload:', fileName)
      controller.abort()
    }
  }

  async function uploadMultipleFiles(
    files: File[],
    onProgress?: (fileName: string, progress: UploadProgress) => void
  ): Promise<FileUploadMetadata[]> {
    const uploads = await Promise.all(
      files.map(file =>
        uploadFile(file, onProgress ? prog => onProgress(file.name, prog) : undefined)
      )
    )
    return uploads.filter((metadata): metadata is FileUploadMetadata => metadata !== null)
  }

  return {
    uploadFile,
    uploadMultipleFiles,
    cancelUpload,
    isUploading,
    uploadProgress,
    error,
    fileProgress
  }
}
